// This tab's songs (ADR 0007): the library list, each song's analysis and its per-song settings
// live in memory only. Nothing about a song is written to browser storage; a reload or closing
// the tab clears it all. Async signatures are kept so a persistent store could return later.
import type { AnalysisResult } from '@/core/analysis/types';
import { readAppSettings } from './settingsRepo';
import type { Song, SongSettings } from './types';

const songs = new Map<string, Song>();
const analyses = new Map<string, AnalysisResult>();
const songSettings = new Map<string, SongSettings>();

export async function getSong(id: string): Promise<Song | undefined> {
  return Promise.resolve(songs.get(id));
}

/** This session's songs, most recently opened first. */
export async function listSongs(): Promise<Song[]> {
  return Promise.resolve([...songs.values()].sort((a, b) => b.lastOpenedAt - a.lastOpenedAt));
}

export async function putSong(song: Song): Promise<void> {
  songs.set(song.id, song);
  return Promise.resolve();
}

export async function markOpened(id: string, now = Date.now()): Promise<void> {
  const song = songs.get(id);
  if (song) song.lastOpenedAt = now;
  return Promise.resolve();
}

/** Removes a song with its analysis and settings from this session. */
export async function deleteSong(id: string): Promise<void> {
  songs.delete(id);
  analyses.delete(id);
  songSettings.delete(id);
  return Promise.resolve();
}

export async function getAnalysis(songId: string): Promise<AnalysisResult | undefined> {
  return Promise.resolve(analyses.get(songId));
}

export async function putAnalysis(songId: string, result: AnalysisResult): Promise<void> {
  analyses.set(songId, result);
  return Promise.resolve();
}

function readSongSettings(songId: string): SongSettings {
  return (
    songSettings.get(songId) ?? {
      songId,
      transpose: 0,
      capo: 0,
      instrument: readAppSettings().defaultInstrument,
      speed: 1,
      loop: null,
    }
  );
}

export async function getSongSettings(songId: string): Promise<SongSettings> {
  return Promise.resolve(readSongSettings(songId));
}

export async function updateSongSettings(
  songId: string,
  patch: Partial<Omit<SongSettings, 'songId'>>,
): Promise<SongSettings> {
  const next = { ...readSongSettings(songId), ...patch, songId };
  songSettings.set(songId, next);
  return Promise.resolve(next);
}

/** Whether this tab holds any songs (a reload or closing the tab would lose them). */
export function hasSongs(): boolean {
  return songs.size > 0;
}

/** Forgets every song (tests). */
export function clearSession(): void {
  songs.clear();
  analyses.clear();
  songSettings.clear();
}
